<script setup lang="ts">
import {computed} from 'vue'
import type {CardsDashboardDeck, CardsDashboardResponse} from '@shared/types'
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from '@/components/ui/card'
import {Button} from '@/components/ui/button'
import {Flame, TrendingDown, TrendingUp, ArrowRight, GraduationCap} from 'lucide-vue-next'
import ActivityHeatmap from './ActivityHeatmap.vue'
import MemoryHealth from './MemoryHealth.vue'
import ProgressForecastChart from './ProgressForecastChart.vue'
import ReviewStack from './ReviewStack.vue'

const props = defineProps<{data: CardsDashboardResponse}>()

const learnedPercent = computed(() =>
  Math.round((props.data.totals.learned / Math.max(props.data.totals.cards, 1)) * 100)
)

const paceText = computed(() => props.data.pace.perDay.toLocaleString(undefined, {maximumFractionDigits: 1}))

const trendKey = computed(() =>
  props.data.pace.trendPerDay > 0.05 ? 'accelerating' : props.data.pace.trendPerDay < -0.05 ? 'decelerating' : 'steady'
)

const dayFormatter = new Intl.DateTimeFormat(undefined, {month: 'short', day: 'numeric'})
const fullFormatter = new Intl.DateTimeFormat(undefined, {month: 'long', day: 'numeric', year: 'numeric'})

function formatDate(date: string | null) {
  if (!date) return '—'
  const value = new Date(`${date}T00:00:00`)
  if (value.getFullYear() !== new Date().getFullYear()) {
    if (value.getTime() - Date.now() > 2 * 365 * 24 * 60 * 60 * 1000) {
      return new Intl.DateTimeFormat(undefined, {year: 'numeric'}).format(value)
    }
    return new Intl.DateTimeFormat(undefined, {month: 'short', year: 'numeric'}).format(value)
  }
  return dayFormatter.format(value)
}

const startedAt = computed(() =>
  props.data.firstStudyAt ? fullFormatter.format(new Date(props.data.firstStudyAt)) : ''
)

const visibleMilestonePercents = computed(() => {
  const currentPercent = (props.data.totals.learned / Math.max(props.data.totals.cards, 1)) * 100
  return new Set([Math.min(100, Math.ceil((currentPercent + 0.001) / 20) * 20), 100])
})

const milestoneChips = computed(() =>
  props.data.timeline.milestones
    .filter((m) => visibleMilestonePercents.value.has(m.percent))
    .map((m) => ({
      percent: m.percent,
      label: formatDate(m.date),
      reached: m.etaDays === 0,
      future: m.etaDays !== null && m.etaDays > 0,
    }))
)

const startedDecks = computed(() => props.data.decks)
type ModuleGroup = {key: string; level: number; unit: number | null; source: string | null; decks: CardsDashboardDeck[]}

const moduleGroups = computed<ModuleGroup[]>(() => {
  const groups = new Map<string, ModuleGroup>()
  for (const deck of startedDecks.value) {
    const source = deck.source ?? null
    const key = `${source ?? ''}:${deck.level}:${deck.unit ?? 0}`
    const group = groups.get(key) ?? {key, level: deck.level, unit: deck.unit, source, decks: []}
    group.decks.push(deck)
    groups.set(key, group)
  }
  return [...groups.values()]
})

const startedCards = computed(() => startedDecks.value.reduce((sum, deck) => sum + deck.n, 0))
const startedDue = computed(() => startedDecks.value.reduce((sum, deck) => sum + deck.dueCount, 0))
const startedMemory = computed(() => {
  if (!startedDecks.value.length) return 0
  return Math.round(
    (startedDecks.value.reduce((sum, deck) => sum + deck.memory.avgRetrievability, 0) / startedDecks.value.length) * 100
  )
})

function moduleName(group: ModuleGroup) {
  if (group.source === 'next') return 'cards.newCards'
  return group.unit ? `cards.modules.level${group.level}.module${group.unit}` : 'home.dashboard.unknownModule'
}

function continueLink(group: ModuleGroup, mode: string) {
  if (group.source === 'next') return `/cards/${mode}?source=next`
  return `/cards/${mode}/${group.level}/${group.unit}`
}

function moduleCards(group: ModuleGroup) {
  return group.decks.reduce((sum, deck) => sum + deck.n, 0)
}

function moduleTotal(group: ModuleGroup) {
  return group.decks.reduce((sum, deck) => sum + deck.m, 0)
}

function moduleDue(group: ModuleGroup) {
  return group.decks.reduce((sum, deck) => sum + deck.dueCount, 0)
}

function moduleMemory(group: ModuleGroup) {
  return Math.round(
    (group.decks.reduce((sum, deck) => sum + deck.memory.avgRetrievability, 0) / Math.max(group.decks.length, 1)) * 100
  )
}
</script>

<template>
  <div class="mx-auto flex w-full max-w-6xl flex-col gap-4 px-4 py-6 md:px-6" data-testid="home-dashboard">
    <div class="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 class="flex items-center gap-2 text-xl font-bold tracking-tight md:text-2xl">
          <GraduationCap class="size-6 text-emerald-500" />
          {{ $t('home.dashboard.title') }}
        </h1>
        <p class="mt-0.5 text-sm text-muted-foreground">{{ $t('home.dashboard.subtitle', {date: startedAt}) }}</p>
      </div>
      <div
        v-if="data.timeline.finishDate"
        class="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3.5 py-1.5 text-xs font-medium text-emerald-400">
        {{ $t('home.dashboard.finishBy', {date: formatDate(data.timeline.finishDate)}) }}
      </div>
    </div>

    <div class="grid grid-cols-2 gap-3 xl:grid-cols-4">
      <Card class="gap-2 py-4">
        <CardContent class="px-4">
          <div class="text-xs text-muted-foreground">{{ $t('home.dashboard.studied') }}</div>
          <div class="mt-1 text-2xl font-bold tabular-nums md:text-3xl">{{ data.totals.learned.toLocaleString() }}</div>
          <div class="text-xs text-muted-foreground">
            {{ $t('home.dashboard.ofTotal', {total: data.totals.cards.toLocaleString()}) }} · {{ learnedPercent }}%
          </div>
        </CardContent>
      </Card>

      <Card class="gap-2 py-4">
        <CardContent class="px-4">
          <div class="text-xs text-muted-foreground">{{ $t('home.dashboard.paceLabel') }}</div>
          <div class="mt-1 flex items-baseline gap-1 text-2xl font-bold tabular-nums md:text-3xl">
            {{ paceText }}
            <span class="text-sm font-normal text-muted-foreground">{{ $t('home.dashboard.perDay') }}</span>
          </div>
          <div
            class="flex items-center gap-1 text-xs"
            :class="
              trendKey === 'accelerating'
                ? 'text-emerald-500'
                : trendKey === 'decelerating'
                  ? 'text-amber-500'
                  : 'text-muted-foreground'
            ">
            <TrendingUp v-if="trendKey === 'accelerating'" class="size-3.5" />
            <TrendingDown v-else-if="trendKey === 'decelerating'" class="size-3.5" />
            {{ $t(`home.dashboard.${trendKey}`) }}
          </div>
        </CardContent>
      </Card>

      <Card class="gap-2 py-4">
        <CardContent class="px-4">
          <div class="text-xs text-muted-foreground">{{ $t('home.dashboard.streakLabel') }}</div>
          <div class="mt-1 flex items-center gap-1.5 text-2xl font-bold tabular-nums md:text-3xl">
            <Flame class="size-6 text-orange-500" />
            {{ data.streak.current }}
          </div>
          <div class="text-xs text-muted-foreground">
            {{ $t('home.dashboard.streakRecord', {n: data.streak.longest}) }}
          </div>
        </CardContent>
      </Card>

      <Card class="gap-2 py-4">
        <CardContent class="px-4">
          <div class="text-xs text-muted-foreground">{{ $t('home.dashboard.memoryTile') }}</div>
          <div class="mt-1 text-2xl font-bold tabular-nums md:text-3xl">
            {{ Math.round(data.memory.avgRetrievability * 100) }}%
          </div>
          <div class="truncate text-xs text-muted-foreground">{{ $t('home.dashboard.memoryTileHint') }}</div>
        </CardContent>
      </Card>
    </div>

    <Card>
      <CardHeader>
        <CardTitle>{{ $t('home.dashboard.chartTitle') }}</CardTitle>
        <CardDescription>{{ $t('home.dashboard.chartDesc') }}</CardDescription>
      </CardHeader>
      <CardContent class="flex flex-col gap-4">
        <ProgressForecastChart
          :past="data.timeline.past"
          :future="data.timeline.future"
          :optimistic="data.timeline.optimistic"
          :pessimistic="data.timeline.pessimistic"
          :total="data.totals.cards"
          :milestones="data.timeline.milestones" />
        <div class="flex flex-wrap gap-1.5">
          <span
            v-for="m in milestoneChips"
            :key="m.percent"
            class="rounded-full border px-2.5 py-0.5 text-xs tabular-nums"
            :class="{
              'border-emerald-500/50 bg-emerald-500/10 text-emerald-400': m.reached,
              'border-chart-1/40 bg-chart-1/10': m.future,
              'text-muted-foreground': !m.future && !m.reached,
            }">
            <b class="font-semibold">{{ m.percent }}%</b> · {{ m.label }}
          </span>
        </div>
      </CardContent>
    </Card>

    <div class="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>{{ $t('home.dashboard.calendarTitle') }}</CardTitle>
        </CardHeader>
        <CardContent>
          <ActivityHeatmap :activity="data.activity" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{{ $t('home.dashboard.memoryTitle') }}</CardTitle>
        </CardHeader>
        <CardContent>
          <MemoryHealth :memory="data.memory" :total-cards="data.totals.cards" :learned-cards="data.totals.learned" />
        </CardContent>
      </Card>
    </div>

    <Card v-if="data.decks.length" data-testid="dashboard-started-modules">
      <CardHeader>
        <CardTitle>{{ $t('home.dashboard.decksTitle') }}</CardTitle>
        <CardDescription>{{ $t('home.dashboard.decksDesc') }}</CardDescription>
        <div class="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span>{{
            $t('home.dashboard.decksSummary', {
              modules: moduleGroups.length,
              cards: startedCards,
              due: startedDue,
              memory: startedMemory,
            })
          }}</span>
        </div>
      </CardHeader>
      <CardContent class="grid grid-cols-1 gap-4">
        <Card
          v-for="group in moduleGroups"
          :key="group.key"
          data-testid="dashboard-module-group"
          class="min-w-0 bg-background/30 p-0">
          <CardHeader class="px-4 pb-3 pt-4">
            <div class="flex items-start justify-between gap-3">
              <CardTitle class="text-base">{{ $t(moduleName(group)) }}</CardTitle>
              <span class="shrink-0 text-xs tabular-nums text-muted-foreground"
                >{{ moduleCards(group) }} / {{ moduleTotal(group) }}</span
              >
            </div>
            <CardDescription>{{
              $t('home.dashboard.moduleSummary', {
                cards: moduleCards(group),
                due: moduleDue(group),
                memory: moduleMemory(group),
              })
            }}</CardDescription>
          </CardHeader>
          <CardContent class="grid gap-3 px-4 pb-4 md:grid-cols-2">
            <div
              v-for="mode in ['listen', 'translate']"
              :key="`${group.key}-${mode}`"
              class="min-w-0 rounded-md border border-border p-3">
              <div class="flex items-center justify-between gap-2 text-sm font-medium">
                <span>{{ $t(`home.dashboard.deckMode.${mode}`) }}</span>
                <span v-if="group.decks.find((deck) => deck.mode === mode)" class="text-xs text-muted-foreground">
                  {{ group.decks.find((deck) => deck.mode === mode)?.percent }}%
                </span>
              </div>
              <template v-if="group.decks.find((deck) => deck.mode === mode)">
                <div class="mt-2 flex items-center gap-3">
                  <div class="min-w-0 flex-1">
                    <ReviewStack
                      :reviews="group.decks.find((deck) => deck.mode === mode)!.memory.nextReviews"
                      :total="group.decks.find((deck) => deck.mode === mode)!.m"
                      :completed="group.decks.find((deck) => deck.mode === mode)!.n" />
                  </div>
                  <span class="-mt-1 shrink-0 self-start text-xs tabular-nums text-muted-foreground">
                    {{ group.decks.find((deck) => deck.mode === mode)!.n }} /
                    {{ group.decks.find((deck) => deck.mode === mode)!.m }}
                  </span>
                </div>
                <div class="mt-2 flex items-center justify-between gap-3 text-xs text-muted-foreground">
                  <div class="flex min-w-0 flex-wrap gap-x-3 gap-y-1">
                    <span>{{
                      $t('home.dashboard.deckMemory', {
                        n: Math.round(group.decks.find((deck) => deck.mode === mode)!.memory.avgRetrievability * 100),
                      })
                    }}</span>
                    <span v-if="group.decks.find((deck) => deck.mode === mode)!.dueCount > 0">{{
                      $t('home.dashboard.deckDue', {n: group.decks.find((deck) => deck.mode === mode)!.dueCount})
                    }}</span>
                    <span v-if="group.decks.find((deck) => deck.mode === mode)!.newCount > 0">{{
                      $t('home.dashboard.deckNew', {n: group.decks.find((deck) => deck.mode === mode)!.newCount})
                    }}</span>
                  </div>
                  <Button
                    asChild
                    variant="secondary"
                    size="sm"
                    class="h-7 shrink-0 px-2 text-xs"
                    data-testid="dashboard-continue">
                    <router-link :to="continueLink(group, mode)">
                      {{ $t('home.dashboard.continue') }}
                      <ArrowRight class="size-3.5" />
                    </router-link>
                  </Button>
                </div>
              </template>
              <span v-else class="mt-3 block text-xs text-muted-foreground">{{
                $t('home.dashboard.noStartedDecks')
              }}</span>
            </div>
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  </div>
</template>
